/**
 * Le bon d'intervention imprimé (lots I2.2 puis « bon refondu », ADR-0014 §12 et §13) : UNE feuille A4 que l'impression du
 * navigateur — ou « Enregistrer en PDF » — reproduit telle quelle, comme la gamme et les planches d'étiquettes QR.
 *
 * Ce module est PUR : il construit ce que le papier dira, sans accès au navigateur ni à l'API. La page n'a plus qu'à
 * le poser sur la feuille. Mise en page validée par Benjamin sur une ébauche le 2026-10-16 : logos et coordonnées de
 * l'entreprise en tête, titre (la nature) et cartouche du numéro, bande de la machine avec le nom que le client lui donne
 * (« AIR01 »), bloc client / date / techniciens, contrôles en trois colonnes, valeurs relevées, entretien réalisé,
 * articles, suites à donner, observations, UNE signature du client et UNE du technicien (tous les techniciens y figurent).
 *
 * DEUX formes d'entrée, jamais mélangées :
 *  - un bon VALIDÉ n'a plus de brouillon (`content` est `null`) : techniciens, contrôles, relevés (avec le libellé et l'unité
 *    de leur compteur), entretien (avec le code du palier), articles et suites viennent de `validated`, ce que la validation
 *    a écrit et que rien ne modifie plus ;
 *  - un bon À VALIDER porte son contenu dans `content` : les libellés des compteurs et des paliers, que le bon ne
 *    garde que par leur identifiant, viennent de la machine (lue par la page), avec un repli lisible — jamais un
 *    identifiant sur le papier.
 * Un brouillon et un bon annulé ne s'impriment pas : un brouillon incomplet imprimé circulerait comme s'il était
 * définitif, et un bon annulé n'a plus de raison d'être signé.
 *
 * L'EN-TÊTE (client, site et son adresse, machine, nature) est une jointure vivante, même pour un bon validé : seuls les
 * identifiants sont figés sur le bon, pas les noms. Un site ou un client renommé depuis change donc le papier d'un bon
 * ancien. Les lignes, relevés, contrôles et suites, eux, sont figés.
 *
 * La feuille suit le MODÈLE de la nature du bon (entretien, dépannage, installation, livraison, location : `bon-modeles.ts`) :
 * ses rubriques, dans l'ordre validé sur les ébauches du 2026-10-16, ses titres, ses colonnes, ses deux signatures. Une
 * livraison peut n'avoir aucune machine : la bande de la machine n'est alors pas imprimée.
 *
 * AUCUN prix, AUCUN total : un bon n'en porte pas (ADR-0011, ADR-0014 §2). Le contact est le texte saisi à la main sur le
 * bon, jamais copié d'une fiche.
 */
import type { BonDetail, FacturationBon, ModeleBon, ValeurStandardNature } from '../api/client';
import { cleLibelleValeur, estReleveEnHeures, valeurAffichee } from './bon-edition';
import {
  LIBELLE_FACTURATION,
  configDe,
  libelleTechniciens,
  type PlaceSerie,
  type Rubrique,
} from './bon-modeles';
import { LIBELLE_GENRE_LIGNE, LIBELLE_SUITE, jourFr, libelleMachine } from './interventions';
import { jourFrDansFuseau } from './validation-bon';

/** Un compteur de la machine, comme la page le lit : de quoi dire le libellé et l'unité d'un relevé du brouillon. */
export interface CompteurConnu {
  readonly id: string;
  readonly label: string;
  readonly unit: string | null;
  /** C'est le compteur des entretiens (les heures totales) : l'entretien réalisé se date à ses heures. */
  readonly drivesMaintenance: boolean;
}

/** Un palier de la gamme rattachée à la machine : de quoi dire le code et le libellé du palier d'un brouillon. */
export interface PalierConnu {
  readonly levelKey: string;
  readonly code: string;
  readonly label: string;
}

/** L'identité de l'entreprise telle que l'en-tête l'imprime : le nom, l'adresse en lignes et les coordonnées. */
export interface IdentiteDocument {
  readonly nom: string;
  readonly adresse: readonly string[];
  readonly telephone: string | null;
  readonly email: string | null;
  readonly siteWeb: string | null;
}

export interface EntreeDocumentBon {
  readonly bon: BonDetail;
  readonly compteurs: readonly CompteurConnu[];
  readonly paliers: readonly PalierConnu[];
  /** L'entreprise, en tête de feuille ; `null` si l'espace ne la dit pas. */
  readonly identite: IdentiteDocument | null;
  /** AAAA-MM-JJ : le jour où le document est édité. */
  readonly edition: string;
  /** Le fuseau des heures écrites (signature du client) ; celui du navigateur par défaut. */
  readonly fuseau?: string;
}

export interface ReleveDocument {
  readonly libelle: string;
  readonly valeur: string;
  readonly unite: string;
  /** Un blanc que le papier laisse à compléter à la main (l'heure « au retour » d'une location). */
  readonly vide?: boolean;
}

export interface EntretienDocument {
  /** Le code du palier réalisé ; `null` quand la gamme a changé depuis et que le palier ne se retrouve plus. */
  readonly code: string | null;
  readonly libelle: string | null;
  /** Les heures du compteur des entretiens à cette date, déjà mises en forme ; `null` si elles ne se lisent pas. */
  readonly heures: string | null;
}

export interface ArticleDocument {
  readonly type: string;
  readonly reference: string | null;
  readonly designation: string;
  readonly note: string | null;
  /** « 2 », « 8 h », « — » : quantité et unité, déjà mises en forme. */
  readonly quantite: string;
  /** Le n° de série d'un matériel livré ou installé ; `null` pour toute autre ligne. */
  readonly serie: string | null;
}

export interface SuiteDocument {
  readonly type: string;
  readonly commentaire: string;
}

export interface ControleDocument {
  readonly libelle: string;
  readonly fait: boolean;
}

export interface DocumentBon {
  /** En-tête : le nom de l'entreprise en gras, puis ses coordonnées en texte (jamais une image de bandeau : trop lourde). */
  readonly entete: { readonly nom: string | null; readonly lignes: readonly string[] };
  readonly numero: string;
  readonly nature: string;
  /** Le modèle de feuille de la nature : il décide des rubriques ci-dessous et de leur ordre. */
  readonly modele: ModeleBon;
  /** Sous contrat ou facturé : la pastille à côté du titre ; `null` tant que le bon ne l'a pas choisi. */
  readonly facturation: { readonly code: FacturationBon; readonly libelle: string } | null;
  readonly numeroPraxedo: string | null;
  /** Faux pour un bon « à valider » : le papier le dit en tête (et en marge de chaque page). */
  readonly valide: boolean;
  readonly client: string | null;
  readonly site: { readonly nom: string; readonly adresse: string | null } | null;
  readonly contact: string | null;
  /** La machine du bon ; `null` pour une livraison faite sur un site seul (la bande n'est alors pas imprimée). */
  readonly machine: {
    readonly designation: string;
    /** Le nom que le client donne à la machine (« AIR01 ») ; `null` s'il n'en donne pas. */
    readonly designationClient: string | null;
  } | null;
  /** JJ/MM/AAAA, ou « — » tant que le jour n'est pas saisi. */
  readonly date: string;
  /** « Technicien », « Techniciens » ou « Livré par » : selon le modèle et le nombre de noms. */
  readonly libelleTechniciens: string;
  readonly techniciens: readonly string[];
  /** L'ordre des rubriques de la feuille, propre au modèle. */
  readonly ordre: readonly Rubrique[];
  /** Le problème signalé et les travaux réalisés d'un dépannage ; `null` hors dépannage. Un texte vide laisse le cadre à remplir. */
  readonly probleme: string | null;
  readonly travaux: string | null;
  /** Le titre de la rubrique des contrôles (« Contrôles de mise en service », « À la réception »…). */
  readonly titreControles: string;
  /** Les contrôles de la nature, dans l'ordre d'impression ; vide : la rubrique n'est pas imprimée. */
  readonly controles: readonly ControleDocument[];
  /** Le nombre de lignes de la grille des contrôles : trois colonnes, remplies colonne par colonne (13 → 5, 5 et 3). */
  readonly controlesLignes: number;
  readonly titreValeurs: string;
  /** Les valeurs relevées en heures (première ligne), puis les autres compteurs (température…) sur la seconde. */
  readonly relevesHeures: readonly ReleveDocument[];
  readonly relevesAutres: readonly ReleveDocument[];
  /** Location : la ligne « au retour », vide, à compléter à la main (mêmes libellés que les heures du départ). */
  readonly relevesRetour: readonly ReleveDocument[];
  readonly entretien: EntretienDocument | null;
  /** Installation : « Mise en service effectuée le … » (le jour du bon) ; `null` hors installation. */
  readonly miseEnService: string | null;
  /** Installation : le rectangle du dossier technique, une case cochée au plus ; `null` hors installation. */
  readonly dossier: { readonly remis: boolean; readonly armoire: boolean } | null;
  /** Location : mise à disposition et retour prévu (JJ/MM/AAAA ou « — ») ; `null` hors location. */
  readonly periode: { readonly du: string; readonly au: string } | null;
  readonly titreLignes: string;
  /** Les colonnes du tableau des lignes : propres au modèle. */
  readonly colonnes: { readonly type: boolean; readonly reference: boolean; readonly serie: PlaceSerie };
  readonly articles: readonly ArticleDocument[];
  /** Livraison : les réserves du client ; `null` hors livraison. Un texte vide laisse le cadre à remplir. */
  readonly reserves: string | null;
  readonly observations: string | null;
  readonly suites: readonly SuiteDocument[];
  /** UNE signature pour tous les techniciens : leurs noms, séparés par des virgules, et le jour de l'intervention. */
  readonly signature: {
    readonly client: string;
    readonly technicien: string;
    readonly techniciens: string;
    readonly date: string | null;
    /** Lot T3 : la clôture faite sur le téléphone — les signatures DESSINÉES remplacent les cadres vides ; `null` sinon. */
    readonly cloture: SignatureCloture | null;
  };
  /** JJ/MM/AAAA. */
  readonly edition: string;
}

export type ResultatDocumentBon =
  | { readonly ok: true; readonly document: DocumentBon }
  | { readonly ok: false; readonly raison: string };

/** La mention en tête (et en marge) d'un bon qui n'est pas encore validé. */
export const MENTION_NON_VALIDE = 'BON NON VALIDÉ';

/** Ce que dit le papier sous la mention : le bon est complet, mais il peut encore être rouvert. */
export const PRECISION_NON_VALIDE = 'À valider : ce bon peut encore être modifié.';

/**
 * Pourquoi un bon ne s'imprime pas ; `null` quand il s'imprime. Une seule règle pour le lien « Imprimer » du détail et
 * pour la page d'impression : le lien n'est proposé que s'il mène à un document.
 */
export function raisonNonImprimable(bon: Pick<BonDetail, 'status' | 'content' | 'validated'>): string | null {
  if (bon.validated !== null) return null;
  if (bon.status === 'CANCELLED') return 'Un bon annulé ne s’imprime pas.';
  if (bon.status === 'DRAFT') {
    return 'Un brouillon ne s’imprime pas : soumettez-le à validation d’abord, pour qu’il soit complet.';
  }
  if (bon.status === 'TO_REVIEW' && bon.content !== null) return null;
  return 'Ce bon n’a pas de contenu à imprimer.';
}

const nettoye = (texte: string | null | undefined): string | null => {
  const t = (texte ?? '').replace(/\s+/g, ' ').trim();
  return t === '' ? null : t;
};

/** L'adresse postale du site, ligne par ligne : voie, complément, puis « code postal ville ». Les lignes vides sautent. */
export function adresseDuSite(site: NonNullable<BonDetail['site']>): readonly string[] {
  const villeEtCode = [nettoye(site.postalCode), nettoye(site.city)].filter((x): x is string => x !== null).join(' ');
  return [nettoye(site.addressLine1), nettoye(site.addressLine2), villeEtCode === '' ? null : villeEtCode].filter(
    (x): x is string => x !== null,
  );
}

/** L'adresse du site sur UNE ligne, comme le papier la montre : « 13 rue de la Vallée, 74600 Seynod ». */
export function adresseSurUneLigne(site: NonNullable<BonDetail['site']>): string | null {
  const lignes = adresseDuSite(site);
  return lignes.length === 0 ? null : lignes.join(', ');
}

/**
 * Les lignes de coordonnées de l'en-tête, sous le nom de l'entreprise : adresse (« voie — code postal ville »), puis
 * « téléphone — courriel », puis le site web. Les lignes vides sautent : l'en-tête ne montre que ce que l'espace a saisi.
 */
export function lignesDeCoordonnees(identite: Pick<IdentiteDocument, 'adresse' | 'telephone' | 'email' | 'siteWeb'>): readonly string[] {
  const adresse = identite.adresse.map((l) => nettoye(l)).filter((l): l is string => l !== null);
  const contact = [nettoye(identite.telephone), nettoye(identite.email)].filter((x): x is string => x !== null);
  return [
    adresse.length === 0 ? null : adresse.join(' — '),
    contact.length === 0 ? null : contact.join(' — '),
    nettoye(identite.siteWeb),
  ].filter((l): l is string => l !== null);
}

// Les heures se relèvent sur la première ligne ; tout autre compteur (température…) sur la seconde. La règle vit avec la saisie
// (`bon-edition.ts`) : l'écran et le papier rangent les heures de la même façon.
export { estReleveEnHeures };

/**
 * Les valeurs qui ne sont pas des heures, avec celles de la FEUILLE du modèle (ADR-0014 §16) : chaque valeur standard
 * (« T° ambiante », « Tension L1 »…) s'imprime, avec sa valeur quand le bon en porte une, sinon avec un « — » à compléter à la
 * main — comme sur l'ébauche validée —, dans l'ordre de la feuille ; les autres valeurs relevées (un compteur propre à la
 * machine) suivent. Une valeur se reconnaît à son libellé, sans tenir compte de la casse, des accents ni de la ponctuation.
 * Celles que la feuille veut sur la ligne des heures (la pression de service d'une mise en service) sortent à part.
 */
export function valeursAvecLaFeuille(
  autres: readonly ReleveDocument[],
  standard: readonly ValeurStandardNature[],
): { readonly avecLesHeures: ReleveDocument[]; readonly autres: ReleveDocument[] } {
  const restantes = [...autres];
  const avecLesHeures: ReleveDocument[] = [];
  const lignes: ReleveDocument[] = [];
  for (const valeur of standard) {
    const cle = cleLibelleValeur(valeur.label);
    const i = restantes.findIndex((r) => cleLibelleValeur(r.libelle) === cle);
    const relevee = i >= 0 ? restantes.splice(i, 1)[0] : undefined;
    (valeur.avecLesHeures === true ? avecLesHeures : lignes).push(relevee ?? { libelle: valeur.label, valeur: '—', unite: '', vide: true });
  }
  return { avecLesHeures, autres: [...lignes, ...restantes] };
}

/** Le nombre de lignes de la grille des contrôles : trois colonnes remplies l'une après l'autre. */
export function lignesDeLaGrille(nombre: number): number {
  return nombre <= 0 ? 0 : Math.ceil(nombre / 3);
}

const SANS_LIBELLE = 'Compteur (libellé indisponible)';

function quantiteEtUnite(quantite: string | null, unite: string | null): string {
  if (quantite === null) return '—';
  const nombre = valeurAffichee(quantite);
  const u = nettoye(unite);
  // Espace insécable : l'unité ne passe jamais seule à la ligne suivante.
  return u === null ? nombre : `${nombre} ${u}`;
}

/**
 * Construit le document d'un bon. Refuse (avec la raison, en français) un brouillon, un bon annulé ou un bon sans contenu.
 * Les relevés, articles et suites sont rendus dans l'ordre où le bon les porte ; rien n'est recalculé, rien n'est ajouté.
 */
export function construireDocumentBon(entree: EntreeDocumentBon): ResultatDocumentBon {
  const { bon } = entree;
  const raison = raisonNonImprimable(bon);
  if (raison !== null) return { ok: false, raison };

  const valide = bon.validated !== null;
  const modele = bon.nature.layout;
  const config = configDe(modele);
  const techniciens = lireTechniciens(bon);
  const entretien = config.palier ? (valide ? entretienValide(bon, entree) : entretienDuBrouillon(bon, entree)) : null;
  // Une feuille sans relevé (la livraison) n'imprime aucune valeur.
  const releves: readonly ReleveClasse[] = !config.releves ? [] : valide ? relevesValides(bon) : relevesDuBrouillon(bon, entree);
  const controles = valide ? controlesValides(bon) : controlesDuBrouillon(bon);
  const date = jourFr(bon.performedOn);
  const details = bon.details;

  // La location relève les heures deux fois : au départ (saisies) et au retour (un blanc à compléter à la main).
  const heures = releves.filter((r) => r.enHeures).map(sansIndicateur);
  const departRetour = config.departRetour && heures.length > 0;
  // Les valeurs standard de la feuille : celles de la ligne des heures (la pression d'une mise en service), puis les autres.
  const feuille = !config.releves
    ? { avecLesHeures: [], autres: [] }
    : valeursAvecLaFeuille(releves.filter((r) => !r.enHeures).map(sansIndicateur), bon.nature.valeursStandard);
  const heuresDuDepart = departRetour ? heures.map((r, i) => ({ ...r, libelle: i === 0 ? `Au départ — ${enBasDeCasse(r.libelle)}` : enBasDeCasse(r.libelle) })) : heures;
  const relevesHeures = [...heuresDuDepart, ...feuille.avecLesHeures];
  const relevesRetour = departRetour
    ? heures.map((r, i) => ({ libelle: i === 0 ? `Au retour — ${enBasDeCasse(r.libelle)}` : enBasDeCasse(r.libelle), valeur: '—', unite: '', vide: true }))
    : [];

  return {
    ok: true,
    document: {
      entete: {
        nom: nettoye(entree.identite?.nom),
        lignes: entree.identite ? lignesDeCoordonnees(entree.identite) : [],
      },
      numero: bon.number,
      nature: bon.nature.label,
      modele,
      facturation: bon.billing ? { code: bon.billing, libelle: LIBELLE_FACTURATION[bon.billing] } : null,
      numeroPraxedo: nettoye(bon.praxedoNumber),
      valide,
      client: nettoye(bon.customer?.name),
      site: bon.site ? { nom: bon.site.name, adresse: adresseSurUneLigne(bon.site) } : null,
      contact: nettoye(bon.contactLabel),
      machine: bon.asset
        ? { designation: libelleMachine(bon.asset), designationClient: nettoye(bon.asset.clientDesignation) }
        : null,
      date,
      libelleTechniciens: libelleTechniciens(modele, techniciens.length),
      techniciens,
      ordre: config.ordre,
      probleme: modele === 'REPAIR' ? (nettoye(details.reportedProblem) ?? '') : null,
      travaux: modele === 'REPAIR' ? (nettoye(details.workDone) ?? '') : null,
      titreControles: config.titreControles,
      controles,
      controlesLignes: lignesDeLaGrille(controles.length),
      titreValeurs: config.titreValeurs,
      relevesHeures,
      // Une feuille sans relevé (la livraison) n'imprime aucune valeur, standard comprise.
      relevesAutres: feuille.autres,
      relevesRetour,
      entretien,
      miseEnService: modele === 'INSTALLATION' ? date : null,
      dossier:
        modele === 'INSTALLATION'
          ? { remis: details.dossier === 'HANDED', armoire: details.dossier === 'IN_CABINET' }
          : null,
      periode:
        modele === 'RENTAL'
          ? { du: jourFr(details.rentalFrom), au: jourFr(details.rentalReturn) }
          : null,
      titreLignes: config.titreLignes,
      colonnes: { type: config.colonneType, reference: config.colonneReference, serie: config.serie },
      articles: valide ? articlesValides(bon) : articlesDuBrouillon(bon),
      reserves: modele === 'DELIVERY' ? (nettoye(details.reservations) ?? '') : null,
      observations: nettoye(bon.observations),
      suites: !config.suites ? [] : valide ? suitesValidees(bon) : suitesDuBrouillon(bon),
      signature: {
        client: config.signatureClient,
        technicien: config.signatureTechnicien,
        techniciens: techniciens.join(', '),
        date: bon.performedOn ? date : null,
        cloture: signatureCloture(bon.cloture ?? null, entree.fuseau),
      },
      edition: jourFr(entree.edition),
    },
  };
}

/** « Heures en charge » → « heures en charge » : un libellé de compteur qui suit « Au départ — » se lit en minuscules. */
function enBasDeCasse(libelle: string): string {
  return libelle === '' ? libelle : libelle.charAt(0).toLowerCase() + libelle.slice(1);
}

function lireTechniciens(bon: BonDetail): readonly string[] {
  const liste = bon.validated !== null ? bon.validated.technicians : (bon.content?.technicians ?? []);
  const noms = liste.map((n) => nettoye(n)).filter((n): n is string => n !== null);
  if (noms.length > 0) return noms;
  // Un bon d'avant la liste ne porte que son libellé.
  const libelle = nettoye(bon.technicianLabel);
  return libelle === null ? [] : [libelle];
}

// ----------------------------------------------------------------------------- relevés : heures d'abord

interface ReleveClasse extends ReleveDocument {
  readonly enHeures: boolean;
}

const sansIndicateur = ({ libelle, valeur, unite }: ReleveClasse): ReleveDocument => ({ libelle, valeur, unite });

// ----------------------------------------------------------------------------- bon VALIDÉ : ce que la validation a écrit

function relevesValides(bon: BonDetail): readonly ReleveClasse[] {
  return (bon.validated?.readings ?? []).map((r) => ({
    libelle: nettoye(r.counterLabel) ?? SANS_LIBELLE,
    valeur: valeurAffichee(r.value) || '—',
    unite: nettoye(r.unit) ?? '',
    enHeures: estReleveEnHeures(r.unit, r.counterLabel),
  }));
}

function controlesValides(bon: BonDetail): readonly ControleDocument[] {
  return (bon.validated?.checks ?? []).map((c) => ({ libelle: nettoye(c.label) ?? '—', fait: c.checked }));
}

function entretienValide(bon: BonDetail, entree: EntreeDocumentBon): EntretienDocument | null {
  const s = bon.validated?.service;
  if (!s) return null;
  const palier = entree.paliers.find((p) => p.code === s.levelCode);
  return {
    code: s.levelCode,
    libelle: nettoye(palier?.label),
    heures: s.counterValue ? valeurAffichee(s.counterValue) : null,
  };
}

function articlesValides(bon: BonDetail): readonly ArticleDocument[] {
  return (bon.validated?.lines ?? []).map((l) => ({
    type: LIBELLE_GENRE_LIGNE[l.kind],
    reference: nettoye(l.reference),
    designation: nettoye(l.designation) ?? '—',
    note: nettoye(l.note),
    quantite: quantiteEtUnite(l.quantity, l.unit),
    serie: nettoye(l.serialNumber),
  }));
}

function suitesValidees(bon: BonDetail): readonly SuiteDocument[] {
  return (bon.validated?.followUps ?? []).map((s) => ({ type: LIBELLE_SUITE[s.kind], commentaire: nettoye(s.comment) ?? '—' }));
}

// ----------------------------------------------------------------------------- bon À VALIDER : le brouillon

function relevesDuBrouillon(bon: BonDetail, entree: EntreeDocumentBon): readonly ReleveClasse[] {
  return (bon.content?.readings ?? [])
    .filter((r) => nettoye(r.value) !== null)
    .map((r) => {
      const compteur = entree.compteurs.find((c) => c.id === r.counterId);
      return {
        libelle: nettoye(compteur?.label) ?? SANS_LIBELLE,
        valeur: valeurAffichee(r.value),
        unite: nettoye(compteur?.unit) ?? '',
        enHeures: estReleveEnHeures(compteur?.unit, compteur?.label),
      };
    });
}

function controlesDuBrouillon(bon: BonDetail): readonly ControleDocument[] {
  return (bon.content?.checks ?? []).map((c) => ({ libelle: nettoye(c.label) ?? '—', fait: c.checked }));
}

function entretienDuBrouillon(bon: BonDetail, entree: EntreeDocumentBon): EntretienDocument | null {
  const cle = bon.content?.levelKey ?? null;
  if (cle === null) return null;
  const palier = entree.paliers.find((p) => p.levelKey === cle);
  // Les heures de l'entretien sont celles du compteur des entretiens, relevé dans ce même bon.
  const designe = entree.compteurs.find((c) => c.drivesMaintenance);
  const releve = designe ? bon.content?.readings.find((r) => r.counterId === designe.id) : undefined;
  const heures = releve && nettoye(releve.value) !== null ? valeurAffichee(releve.value) : null;
  return { code: palier?.code ?? null, libelle: nettoye(palier?.label), heures };
}

function articlesDuBrouillon(bon: BonDetail): readonly ArticleDocument[] {
  return (bon.content?.lines ?? []).map((l) => ({
    type: LIBELLE_GENRE_LIGNE[l.kind],
    reference: nettoye(l.reference),
    designation: nettoye(l.designation) ?? '—',
    note: nettoye(l.note),
    quantite: quantiteEtUnite(l.quantity, l.unit),
    serie: nettoye(l.serialNumber),
  }));
}

function suitesDuBrouillon(bon: BonDetail): readonly SuiteDocument[] {
  return (bon.content?.followUps ?? []).map((s) => ({ type: LIBELLE_SUITE[s.kind], commentaire: nettoye(s.comment) ?? '—' }));
}

// ----------------------------------------------------------------------------- valeurs relevées : la grille

/** Au plus ce nombre de valeurs par ligne : les libellés (« Au départ — heures de fonctionnement ») ont besoin de place. */
export const VALEURS_PAR_LIGNE = 3;

const morceaux = <T>(liste: readonly T[], taille: number): T[][] => {
  const lots: T[][] = [];
  for (let i = 0; i < liste.length; i += taille) lots.push(liste.slice(i, i + taille));
  return lots;
};

/** « Tension L1 » → « tension » : le nom d'une famille de PHASES (trois valeurs, L1, L2 et L3). Nul pour tout autre libellé. */
function familleDePhases(libelle: string): string | null {
  const m = /^(.+?)\s+L[1-3]$/i.exec(libelle.trim());
  return m?.[1] ? m[1].trim().toLowerCase() : null;
}

/**
 * Les valeurs qui ne sont pas des heures, rangées en lignes : chaque FAMILLE DE PHASES (« Tension L1, L2, L3 », « Intensité L1,
 * L2, L3 ») sur sa propre ligne, comme sur l'ébauche validée ; les autres (températures, pression…) ensemble, trois par ligne,
 * AVANT les familles. Une famille d'une seule valeur n'en est pas une : elle rejoint les autres.
 */
export function lignesDeValeurs(releves: readonly ReleveDocument[]): ReleveDocument[][] {
  const effectifs = new Map<string, number>();
  for (const r of releves) {
    const f = familleDePhases(r.libelle);
    if (f !== null) effectifs.set(f, (effectifs.get(f) ?? 0) + 1);
  }
  const seuls: ReleveDocument[] = [];
  const familles = new Map<string, ReleveDocument[]>();
  for (const r of releves) {
    const f = familleDePhases(r.libelle);
    if (f !== null && (effectifs.get(f) ?? 0) >= 2) {
      const lot = familles.get(f) ?? [];
      lot.push(r);
      familles.set(f, lot);
    } else {
      seuls.push(r);
    }
  }
  return [...morceaux(seuls, VALEURS_PAR_LIGNE), ...[...familles.values()].flatMap((lot) => morceaux(lot, VALEURS_PAR_LIGNE))];
}

/**
 * Toutes les lignes de la rubrique des valeurs, dans l'ordre du papier : les heures (au départ, pour une location), la ligne
 * « au retour » à compléter à la main, puis les autres valeurs.
 */
export function rangeesDeValeurs(doc: Pick<DocumentBon, 'relevesHeures' | 'relevesRetour' | 'relevesAutres'>): ReleveDocument[][] {
  return [
    ...morceaux(doc.relevesHeures, VALEURS_PAR_LIGNE),
    ...morceaux(doc.relevesRetour, VALEURS_PAR_LIGNE),
    ...lignesDeValeurs(doc.relevesAutres),
  ];
}

export interface CelluleValeur {
  readonly releve: ReleveDocument;
  /** Le nombre de colonnes de la grille que la valeur occupe. */
  readonly span: number;
}

export interface GrilleValeurs {
  readonly colonnes: number;
  readonly lignes: readonly (readonly CelluleValeur[])[];
}

/**
 * La grille des valeurs : toutes les lignes partagent les MÊMES colonnes (« aligne, ça fait brouillon », Benjamin, 2026-10-16),
 * et une ligne plus courte s'étale sur les colonnes quand le compte tombe juste (deux valeurs sur quatre colonnes : deux
 * colonnes chacune ; deux valeurs sur trois colonnes : une chacune, la troisième reste libre).
 */
export function grilleDesValeurs(lignes: readonly (readonly ReleveDocument[])[]): GrilleValeurs {
  const pleines = lignes.filter((l) => l.length > 0);
  const colonnes = Math.max(1, ...pleines.map((l) => l.length));
  return {
    colonnes,
    lignes: pleines.map((l) => {
      const span = colonnes % l.length === 0 ? colonnes / l.length : 1;
      return l.map((releve) => ({ releve, span }));
    }),
  };
}

// ----------------------------------------------------------------------------- nom du document

/**
 * Le nom du document : « BI-2026-00001 – KAESER-ESD442SFC/1312-ESD.3 – AIR01 ». C'est le titre de la page, donc le nom que
 * Chrome et Edge proposent au PDF : le numéro d'abord (on cherche un bon par son numéro), jamais le mot « Bon » (BI veut déjà
 * dire bon d'intervention), puis la machine, puis le nom que le client lui donne — la désignation s'arrête là quand il n'en
 * donne pas. Les caractères que Windows refuse dans un nom de fichier (la machine peut en porter : « / ») sont remplacés ; la
 * mention « non validé » y figure : on ne confond pas un bon à valider avec un bon validé.
 */
export function nomDocumentBon(doc: DocumentBon): string {
  // Sans machine (une livraison sur un site seul), c'est le site qui dit de quoi il s'agit.
  const base = [doc.numero, doc.machine?.designation ?? doc.site?.nom ?? null, doc.machine?.designationClient ?? null]
    .filter((x): x is string => x !== null && x !== '')
    .join(' – ');
  const nom = `${base}${doc.valide ? '' : ' (non validé)'}`;
  return nom.replace(/[\\/:*?"<>|]+/g, '-').replace(/\s+/g, ' ').trim();
}

// ----------------------------------------------------------------------------- une page ou deux ?

/**
 * La capacité d'UNE page A4, en « lignes » de la feuille. C'est une ESTIMATION, calée sur des PDF réels imprimés par Edge
 * à partir de l'application (2026-10-16) : avec 13 contrôles, trois techniciens, trois relevés et une suite, 19 articles tiennent
 * sur une page et 20 passent sur deux ; SANS contrôle, 25 tiennent et 26 passent (voir `bon-document.spec.ts`, qui rejoue
 * exactement ces bons). La mise en page décide en dernier : l'estimation ne
 * sert qu'à avertir à l'écran, jamais à réduire le texte. Le texte garde sa taille ; au-delà de la capacité le bon passe sur
 * une 2ᵉ page, coupée proprement — jamais une ligne coupée en deux, jamais les signatures seules.
 */
export const CAPACITE_UNE_PAGE = 45;

/** Les lignes que l'en-tête, le titre, la bande de la machine, les titres de rubrique, les signatures et le pied prennent toujours. */
export const LIGNES_FIXES = 15;

const lignesDeTexte = (texte: string | null, parLigne: number): number =>
  texte === null ? 0 : Math.max(1, Math.ceil(texte.length / parLigne));

/** Le poids du document, en lignes de la feuille (voir `CAPACITE_UNE_PAGE`). */
export function poidsDuDocument(doc: DocumentBon): number {
  let poids = LIGNES_FIXES;
  // Le bloc client / date : la colonne de gauche prend 4 lignes, celle de droite 1 + un technicien par ligne.
  poids += Math.max(0, 1 + doc.techniciens.length - 4);
  if (doc.controles.length > 0) poids += 1 + doc.controlesLignes;
  if (doc.relevesHeures.length > 0 || doc.relevesAutres.length > 0) {
    poids += 1 + rangeesDeValeurs(doc).length;
  }
  if (doc.entretien) poids += 1;
  // Les rubriques propres aux modèles : un cadre de texte (titre + au moins deux lignes), une ligne de période ou de dossier.
  for (const texte of [doc.probleme, doc.travaux, doc.reserves]) {
    if (texte !== null) poids += 1 + Math.max(2, lignesDeTexte(texte === '' ? null : texte, 110));
  }
  if (doc.miseEnService !== null) poids += 1;
  if (doc.dossier !== null) poids += 1;
  if (doc.periode !== null) poids += 1;
  for (const a of doc.articles) poids += lignesDeTexte(a.designation, 60) + (a.note ? lignesDeTexte(a.note, 80) : 0);
  for (const s of doc.suites) poids += lignesDeTexte(s.commentaire, 85);
  poids += lignesDeTexte(doc.observations, 120);
  return poids;
}

/** 1 si le bon tient sur une page, sinon le nombre de pages estimé (2 d'abord). */
export function pagesEstimees(doc: DocumentBon): number {
  return Math.max(1, Math.ceil(poidsDuDocument(doc) / CAPACITE_UNE_PAGE));
}

/** La phrase de l'écran sur la longueur du bon, ou `null` quand il tient sur une page. */
export function phraseLongueur(doc: DocumentBon): string | null {
  const pages = pagesEstimees(doc);
  if (pages <= 1) return null;
  return `Ce bon est long : prévoyez ${pages} pages environ. Le texte garde sa taille, aucune ligne n’est coupée et les signatures ne se retrouvent jamais seules.`;
}

/** La clôture du téléphone pour le papier (lot T3) : le client a signé (nom, instant, image) ou il était absent (motif). */
export interface SignatureCloture {
  readonly nomClient: string | null;
  /** « 27/10/2026 » : le jour seul, jamais l'heure (le client ne voit aucune heure). */
  readonly dateClient: string | null;
  /** Le motif d’absence du client ; `null` quand il a signé. */
  readonly absent: string | null;
  readonly imageClientId: string | null;
  readonly imageTechnicienId: string | null;
}

export function signatureCloture(c: BonDetail['cloture'], fuseau?: string): SignatureCloture | null {
  if (c === null) return null;
  const absent = c.absentReason !== null;
  return {
    nomClient: absent ? null : c.signerName,
    dateClient: absent ? null : jourFrDansFuseau(c.signedAt ?? c.closedAt, fuseau),
    absent: c.absentReason,
    imageClientId: absent ? null : c.clientSignatureFileId,
    imageTechnicienId: c.technicianSignatureFileId,
  };
}
